import { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Maria Lucia Santos",
      age: 67,
      profession: "Aposentada",
      location: "São Paulo, SP",
      image: "https://images.unsplash.com/photo-1602409013704-008631bea714",
      imageAlt:
        "Senhora brasileira de 67 anos sorrindo, cabelos grisalhos, usando blusa azul clara em ambiente doméstico",
      rating: 5,
      content: `Estava com muitas dívidas acumuladas e não sabia mais o que fazer. A equipe da TJEP me ajudou a renegociar tudo com desconto de 70%. Hoje durmo tranquila sabendo que minha aposentadoria está protegida.`,
      result: "Reduziu dívidas em 70%",
      service: "Renegociação de Dívidas",
      beforeAmount: "R$ 45.000",
      afterAmount: "R$ 13.500",
    },
    {
      id: 2,
      name: "Anderson Pereira Silva",
      age: 42,
      profession: "Trabalhador CLT",
      location: "Rio de Janeiro, RJ",
      image: "https://images.unsplash.com/photo-1714974528889-d51109fb6ae9",
      imageAlt:
        "Homem brasileiro de 42 anos sorrindo, cabelos escuros, usando camisa social branca em escritório",
      rating: 5,
      content: `Precisava de dinheiro urgente para uma emergência médica na família. O empréstimo consignado foi aprovado em menos de 24 horas. Sem burocracia, sem pegadinhas. Recomendo de olhos fechados!`,
      result: "Aprovação em 24h",
      service: "Empréstimo para CLT",
      beforeAmount: "Emergência",
      afterAmount: "R$ 25.000 liberados",
    },
    {
      id: 3,
      name: "Carlos Roberto Lima",
      age: 61,
      profession: "Aposentado",
      location: "Salvador, BA",
      image: "https://images.unsplash.com/photo-1669642550301-bbba2d5f09c8",
      imageAlt:
        "Homem brasileiro de 61 anos sorrindo, cabelos grisalhos, usando camisa polo azul em ambiente descontraído",
      rating: 5,
      content: `Antecipei meu FGTS para reformar a casa e ficou perfeito. O processo foi super rápido e transparente. Recebi o dinheiro em 2 dias e pude realizar o sonho da reforma sem me endividar.`,
      result: "FGTS liberado em 48h",
      service: "Antecipação FGTS",
      beforeAmount: "Sonho adiado",
      afterAmount: "R$ 18.000 liberados",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  const handlePrevious = () => {
    setCurrentTestimonial(
      (prev) => (prev - 1 + testimonials?.length) % testimonials?.length,
    );
  };

  const handleNext = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Olá! Vi os depoimentos e gostaria de uma análise gratuita da minha situação financeira.",
    );
    window.open(`https://wa.me/553397070278?text=${message}`, "_blank");
  };

  const current = testimonials?.[currentTestimonial];

  return (
    <section
      id="depoimentos"
      className="py-20 bg-gradient-to-br from-slate-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="MessageSquare" size={16} />
            <span>Depoimentos Reais</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Histórias de Sucesso dos Nossos Clientes
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Mais de 15.000 brasileiros já transformaram suas vidas financeiras
            conosco. Veja alguns depoimentos reais de quem confia na TJEP
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="relative">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-12 lg:gap-16">
              {/* Testimonial Content */}
              <div className="space-y-6 flex-1 max-w-2xl">
                {/* Service Badge */}
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  <Icon name="Star" size={14} />
                  <span>{current?.service}</span>
                </div>

                {/* Quote */}
                <div className="relative">
                  <blockquote className="text-lg md:text-xl text-foreground leading-relaxed pl-8 pr-8">
                    "{current?.content}"
                  </blockquote>
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-1">
                  {[...Array(current?.rating)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={20}
                      className="text-accent fill-current"
                    />
                  ))}
                </div>

                {/* Results */}
                <div className="bg-success/10 rounded-xl p-4">
                  <div className="flex space-x-2 mb-2">
                    <Icon
                      name="TrendingUp"
                      size={20}
                      className="text-success"
                    />
                    <span className="font-semibold text-success">
                      Resultado Alcançado
                    </span>
                  </div>
                  <p className="text-foreground font-medium">
                    {current?.result}
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-sm">
                    <span className="text-muted-foreground">
                      Antes: {current?.beforeAmount}
                    </span>
                    <Icon
                      name="ArrowRight"
                      size={16}
                      className="text-muted-foreground flex-shrink-0"
                    />
                    <span className="text-success font-medium">
                      Depois: {current?.afterAmount}
                    </span>
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div className="text-center lg:text-right flex-shrink-0">
                <div className="relative inline-block mb-6">
                  <Image
                    src={current?.image}
                    alt={current?.imageAlt}
                    className="w-32 h-32 rounded-full object-cover mx-auto lg:mx-0 border-4 border-white shadow-lg"
                  />

                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-success rounded-full flex items-center justify-center border-2 border-white">
                    <Icon name="Check" size={16} className="text-white" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-foreground">
                    {current?.name}
                  </h3>
                  <p className="text-muted-foreground">
                    {current?.profession}, {current?.age} anos
                  </p>
                  <div className="flex items-center justify-center lg:justify-end space-x-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={16} />
                    <span>{current?.location}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={handlePrevious}
            className="absolute -left-6 lg:-left-16 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg border flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 z-10"
          >
            <Icon name="ChevronLeft" size={20} className="text-foreground" />
          </button>

          <button
            onClick={handleNext}
            className="absolute -right-6 lg:-right-16 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg border flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 z-10"
          >
            <Icon name="ChevronRight" size={20} className="text-foreground" />
          </button>
        </div>

        {/* Testimonial Indicators */}
        <div className="flex justify-center space-x-2 mt-8">
          {testimonials?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentTestimonial(index)}
              className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                index === currentTestimonial
                  ? "bg-primary"
                  : "bg-border hover:bg-primary/50"
              }`}
            />
          ))}
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          <div className="text-center bg-white p-6 rounded-xl shadow-subtle">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={24} className="text-primary" />
            </div>
            <p className="text-2xl font-bold text-foreground">15.000+</p>
            <p className="text-muted-foreground">Clientes Atendidos</p>
          </div>

          <div className="text-center bg-white p-6 rounded-xl shadow-subtle">
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="ThumbsUp" size={24} className="text-success" />
            </div>
            <p className="text-2xl font-bold text-foreground">98%</p>
            <p className="text-muted-foreground">Satisfação</p>
          </div>

          <div className="text-center bg-white p-6 rounded-xl shadow-subtle">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="MapPin" size={24} className="text-accent" />
            </div>
            <p className="text-2xl font-bold text-foreground">26</p>
            <p className="text-muted-foreground">Estados Atendidos</p>
          </div>

          <div className="text-center bg-white p-6 rounded-xl shadow-subtle">
            <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="Clock" size={24} className="text-secondary" />
            </div>
            <p className="text-2xl font-bold text-foreground">24h</p>
            <p className="text-muted-foreground">Tempo Médio</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Seja o Próximo Caso de Sucesso
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Junte-se a milhares de brasileiros que já transformaram suas vidas
              financeiras. Sua análise gratuita está a um clique de distância
            </p>
            <button
              onClick={handleWhatsAppClick}
              className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-8 py-4 rounded-xl font-semibold hover:bg-primary/90 transition-colors duration-200 shadow-cta"
            >
              <Icon name="MessageCircle" size={20} />
              <span>Quero Minha Análise Gratuita</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
